import PropTypes from 'prop-types';
import React from 'react';
import { gql } from '@selfdetermine/graphql';
import { useMutation, useLazyQuery } from '@selfdetermine/react-hooks';
import { useHistory, useLocation, Switch, Route, BrowserRouter } from 'react-router-dom';
import { Section, Container, Columns, Level, Button } from '@selfdetermine/react-elements';
import { Form } from '@selfdetermine/react-forms';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _templateObject3() {
  var data = _taggedTemplateLiteral(["\n      query User {\n        viewer {\n          self {\n            email\n            admin\n            roles {\n              name\n            }\n          }\n        }\n      }\n    "]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteral(["\n      mutation {\n        logoutUser {\n          logout\n        }\n      }\n    "]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n      mutation($c: AuthProviderCredentialsInput) {\n        loginUser(credentials: $c) {\n          email\n          roles {\n            name\n          }\n        }\n      }\n    "]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Custom react hook that exposes the ability to login a user. Returns a tuple where the first
 * value contains a function that performs a login request. The second value in the tuple includes
 * metadata about the request such as if it's loading, response, and error data.
 *
 * @param {object} [options] - Mutation options @see https://www.apollographql.com/docs/react/api/react-hooks/#options-2
 * @returns {[func, object]} - login function and request metadata
 */

var useLogin = function useLogin() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _useMutation = useMutation(gql(_templateObject()), options),
      _useMutation2 = _slicedToArray(_useMutation, 2),
      mutate = _useMutation2[0],
      meta = _useMutation2[1];

  var login = function login() {
    var form = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var requestOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return mutate(_objectSpread2({
      variables: {
        c: form
      }
    }, requestOptions));
  };

  return [login, meta];
};
var useLogout = function useLogout() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _useMutation3 = useMutation(gql(_templateObject2()), options),
      _useMutation4 = _slicedToArray(_useMutation3, 2),
      mutate = _useMutation4[0],
      meta = _useMutation4[1];

  var logout = function logout() {
    return mutate({
      variables: {
        logout: true
      }
    });
  };

  return [logout, meta];
};
var useCurrentUser = function useCurrentUser() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _useLazyQuery = useLazyQuery(gql(_templateObject3()), options),
      _useLazyQuery2 = _slicedToArray(_useLazyQuery, 2),
      query = _useLazyQuery2[0],
      meta = _useLazyQuery2[1];

  var getUser = function getUser() {
    var form = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var requestOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return query(_objectSpread2({
      variables: form
    }, requestOptions));
  };

  return [getUser, meta];
};

/**
 * Actual authentication app. This allows users to login/register. Once they're authenticated then
 * the `onLogin` callback is called which should be used to refetch the user's data and then continue
 * to show the rest of the app.
 *
 * @param {object} props - Component props
 * @param {Component} [props.logo] - logo component
 * @param {function} props.onLogin - Callback for when user is authenticated
 * @param {function} props.passwordResetUrl - Url of password reset form
 * @returns {function} Component
 */

var App = function App(_ref) {
  var onLogin = _ref.onLogin,
      passwordResetUrl = _ref.passwordResetUrl,
      Logo = _ref.logo;
  var history = useHistory();
  var location = useLocation();

  var _React$useState = React.useState(null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      initialLocation = _React$useState2[0],
      setInitialLocation = _React$useState2[1];

  var _useLogin = useLogin(),
      _useLogin2 = _slicedToArray(_useLogin, 2),
      login = _useLogin2[0],
      loginForm = _useLogin2[1];
  /**
   * Handles authentication form submit. Once the user is logged in we redirect them back to
   * wherever they came from.
   * @param {object} form - Login form data
   */


  var handleLogin = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(form) {
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 3;
              return login(form);

            case 3:
              history.replace(initialLocation);

              if (typeof onLogin === "function") {
                onLogin();
              }

              _context.next = 9;
              break;

            case 7:
              _context.prev = 7;
              _context.t0 = _context["catch"](0);

            case 9:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[0, 7]]);
    }));

    return function handleLogin(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  /**
   * When the app is first loaded we want to store the user's original location so we can redirect
   * them back after they're authenticated, we then redirect them to the login page.
   */


  React.useEffect(function () {
    setInitialLocation(location && location.pathname !== "/login" ? location : "/");
    history.replace("/login");
  }, []);
  return /*#__PURE__*/React.createElement(Section, {
    size: "large"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Columns, {
    centered: true
  }, /*#__PURE__*/React.createElement(Columns.Column, {
    size: "4"
  }, !!Logo && /*#__PURE__*/React.createElement(Level, {
    className: "mb-4"
  }, /*#__PURE__*/React.createElement(Level.Item, null, /*#__PURE__*/React.createElement(Logo, null))), /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    path: "/login"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: handleLogin,
    loading: loginForm.loading,
    error: loginForm.error
  }, /*#__PURE__*/React.createElement(Form.Input, {
    type: "email",
    name: "email",
    label: "Email Address",
    required: true,
    autoFocus: true
  }), /*#__PURE__*/React.createElement(Form.Input, {
    type: "password",
    name: "password",
    label: "Password",
    required: true
  }), /*#__PURE__*/React.createElement(Level, null, /*#__PURE__*/React.createElement(Level.Left, null, /*#__PURE__*/React.createElement(Level.Item, null, /*#__PURE__*/React.createElement(Form.SubmitButton, {
    color: "primary"
  }, "Sign In"))), !!passwordResetUrl && /*#__PURE__*/React.createElement(Level.Right, null, /*#__PURE__*/React.createElement(Level.Item, null, /*#__PURE__*/React.createElement(Button, {
    color: "text",
    as: "a",
    href: passwordResetUrl
  }, "Forgot password?")))))))))));
};

App.propTypes = {
  logo: PropTypes.element,
  onLogin: PropTypes.func,
  passwordResetUrl: PropTypes.func
};
/**
 * Wraps the Authentication App with a Router to manage different authentication views.
 *
 * @param {object} props - App props
 * @returns {function} Component
 */

var AuthenticationApp = function AuthenticationApp(_ref3) {
  var onLogin = _ref3.onLogin,
      logo = _ref3.logo,
      passwordResetUrl = _ref3.passwordResetUrl;
  return /*#__PURE__*/React.createElement(BrowserRouter, null, /*#__PURE__*/React.createElement(App, {
    onLogin: onLogin,
    logo: logo,
    passwordResetUrl: passwordResetUrl
  }));
};
AuthenticationApp.propTypes = {
  logo: PropTypes.element,
  onLogin: PropTypes.func,
  passwordResetUrl: PropTypes.func
};

/**
 * React context containing current user's authentication information.
 */

var AuthenticationContext = /*#__PURE__*/React.createContext({});
/**
 * Returns the current app's authentication context.
 *
 * @returns {object} Authentication context
 */

var useAuthenticationContext = function useAuthenticationContext() {
  return React.useContext(AuthenticationContext);
};
/**
 * Wraps contents in API authentication. If the user is not authenticated, then the user is shown
 * the authentication flow until they're properly authenticated. Once the user is authenticated
 * the rest of app is returned and authentication information is exposed using the AuthenticationContext.
 *
 * This provider must be a descendant of the `ClientProvider` found in `@selfdetermine/react-hooks`.
 *
 * @example
 *
 * <ClientProvider client={client}>
 *   <AuthenticationProvider>
 *     <App />
 *   </AuthenticationProvider>
 * </ClientProvider>
 *
 *
 * @param {object} props - Provider props
 * @param {string} props.passwordResetUrl - Url of password reset form
 * @param {node|function} props.logo - Logo component
 * @param {node|function} props.children - App components
 * @returns {function} AuthenticationContext Provider component
 */

var AuthenticationProvider = function AuthenticationProvider(_ref) {
  var _currentUser$data, _currentUser$data$vie;

  var passwordResetUrl = _ref.passwordResetUrl,
      logo = _ref.logo,
      children = _ref.children;

  var _useCurrentUser = useCurrentUser(),
      _useCurrentUser2 = _slicedToArray(_useCurrentUser, 2),
      getCurrentUser = _useCurrentUser2[0],
      currentUser = _useCurrentUser2[1];

  React.useEffect(function () {
    if (!currentUser.called) {
      getCurrentUser();
    }
  }, [getCurrentUser, currentUser.called]);
  var user = currentUser === null || currentUser === void 0 ? void 0 : (_currentUser$data = currentUser.data) === null || _currentUser$data === void 0 ? void 0 : (_currentUser$data$vie = _currentUser$data.viewer) === null || _currentUser$data$vie === void 0 ? void 0 : _currentUser$data$vie.self;

  if (!currentUser.called || currentUser.loading) {
    return false;
  }

  if (!user) {
    return /*#__PURE__*/React.createElement(AuthenticationApp, {
      onLogin: function onLogin() {
        return currentUser.refetch();
      },
      passwordResetUrl: passwordResetUrl,
      logo: logo
    });
  }

  return /*#__PURE__*/React.createElement(AuthenticationContext.Provider, {
    value: {
      user: user
    }
  }, /*#__PURE__*/React.createElement(React.Fragment, null, children));
};
AuthenticationProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  logo: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  passwordResetUrl: PropTypes.string
};

export { AuthenticationContext, AuthenticationProvider, useAuthenticationContext, useCurrentUser, useLogin, useLogout };
